import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchPoEmailQueue, sendABNPOEmail } from '../utils/api';
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';

const pageStyle = {
  padding: '16px 18px 32px',
  width: '100%',
  boxSizing: 'border-box',
  color: '#f1f5f9',
};

const headerWrapStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
  flexWrap: 'wrap',
  gap: 12,
};

const titleStyle = {
  fontSize: '1.4rem',
  fontWeight: 700,
  color: '#f1f5f9',
  letterSpacing: '-0.02em',
  margin: 0,
};

const subtitleStyle = {
  fontSize: '0.78rem',
  color: 'rgba(255,255,255,0.45)',
  marginTop: 2,
};

const bulkBtnStyle = {
  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  padding: '12px 22px',
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 4px 18px rgba(239,68,68,0.3)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  transition: 'all 0.2s',
};

const statsCardStyle = {
  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  border: '1px solid rgba(34,211,238,0.15)',
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: 18,
  position: 'relative',
};
const statsGradientBarStyle = {
  height: 3,
  background: 'linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)',
};
const statsInnerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px 20px 6px',
  flexWrap: 'wrap',
  gap: 12,
};
const statsLabelStyle = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 4,
};
const statsAmountStyle = {
  fontSize: '1.5rem',
  fontWeight: 800,
  color: '#f1f5f9',
  fontFamily: 'monospace',
};
const pendingBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  height: 32,
  padding: '0 12px',
  borderRadius: 9999,
  background: 'rgba(34,211,238,0.15)',
  color: '#22d3ee',
  fontSize: '0.95rem',
  fontWeight: 700,
  border: '1px solid rgba(34,211,238,0.3)',
};
const statsCountStyle = {
  padding: '4px 20px 14px',
  fontSize: '0.72rem',
  color: 'rgba(255,255,255,0.35)',
};

const searchWrapStyle = { position: 'relative', marginBottom: 16 };
const searchInputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(34,211,238,0.15)',
  borderRadius: 10,
  padding: '10px 14px 10px 38px',
  fontSize: '0.85rem',
  color: '#f1f5f9',
  outline: 'none',
  boxSizing: 'border-box',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'rgba(34,211,238,0.5)',
  pointerEvents: 'none',
};

const emptyStateStyle = {
  textAlign: 'center',
  padding: '60px 24px',
  background: 'rgba(255,255,255,0.02)',
  border: '1px dashed rgba(255,255,255,0.1)',
  borderRadius: 16,
  color: 'rgba(255,255,255,0.5)',
};
const titleBlockStyle = { display: 'flex', flexDirection: 'column' };
const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: 12,
};

const cardStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(34,211,238,0.15)',
  borderRadius: 12,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  transition: 'all 0.2s',
};

const cardPoIdStyle = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#f1f5f9',
  fontFamily: 'monospace',
};

const cardEmailStyle = {
  fontSize: '0.78rem',
  color: 'rgba(255,255,255,0.55)',
  wordBreak: 'break-all',
};

const cardFileStyle = {
  fontSize: '0.7rem',
  color: 'rgba(34,211,238,0.6)',
  fontFamily: 'monospace',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  background: 'rgba(34,211,238,0.05)',
  padding: '4px 8px',
  borderRadius: 6,
};

/* Confirm modal */
const overlayStyle = {
  position: 'fixed', inset: 0, top: 'var(--fr-modal-top, 0px)',
  background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1050, padding: 20,
};
const confirmModalStyle = {
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  border: '1px solid rgba(34,211,238,0.25)',
  borderRadius: 16, width: '100%', maxWidth: 460, overflow: 'hidden',
  boxShadow: '0 25px 80px rgba(0,0,0,0.6)',
};
const confirmHeaderStyle = { padding: '20px 24px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)' };
const confirmBodyStyle = { padding: '16px 24px', color: 'rgba(255,255,255,0.75)', fontSize: '0.88rem', lineHeight: 1.5 };
const confirmFooterStyle = {
  padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 10,
  background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.06)',
};
const cancelBtnStyle = {
  background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)',
  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
  padding: '10px 18px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
};
const confirmBtnStyle = {
  background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)',
  color: '#0f172a', border: 'none', borderRadius: 10,
  padding: '10px 22px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(34,211,238,0.3)',
};

/* Animated sending card */
const sendingCardStyle = {
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  border: '1px solid rgba(34,211,238,0.3)',
  borderRadius: 18, padding: 32, textAlign: 'center',
  position: 'relative', overflow: 'hidden',
  boxShadow: '0 10px 40px rgba(0,0,0,0.4), 0 0 60px rgba(34,211,238,0.08)',
};
const sendingPulseStyle = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)', width: 200, height: 200,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(34,211,238,0.2) 0%, transparent 70%)',
  animation: 'snlPulse 2s ease-in-out infinite', pointerEvents: 'none',
};
const sendingIconStyle = {
  fontSize: '3rem', marginBottom: 16, display: 'inline-block',
  animation: 'snlBounce 1.4s ease-in-out infinite',
};
const sendingTitleStyle = {
  fontSize: '1.2rem', fontWeight: 700, color: '#f1f5f9',
  marginBottom: 8, position: 'relative',
};
const sendingNameStyle = {
  fontSize: '1.4rem', fontWeight: 800,
  background: 'linear-gradient(90deg, #22d3ee, #3b82f6)',
  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  backgroundClip: 'text', marginBottom: 14,
  letterSpacing: '-0.02em', position: 'relative',
};
const sendingRemainingStyle = {
  fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)',
  marginBottom: 18, position: 'relative',
};
const progressTrackStyle = {
  background: 'rgba(255,255,255,0.06)', borderRadius: 9999,
  height: 8, overflow: 'hidden', margin: '0 auto',
  maxWidth: 400, position: 'relative',
};
const progressFillStyle = (pct) => ({
  height: '100%', width: `${pct}%`,
  background: 'linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)',
  transition: 'width 0.5s ease', borderRadius: 9999,
});
const progressCountStyle = {
  fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)',
  marginTop: 8, position: 'relative',
};

/* Log */
const logCardStyle = {
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 14, padding: 16, marginTop: 18,
};
const logHeaderStyle = {
  display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', marginBottom: 12,
};
const logTitleStyle = { fontSize: '0.85rem', fontWeight: 700, color: '#f1f5f9' };
const logCloseBtnStyle = {
  background: 'transparent', border: 'none',
  color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '0.78rem',
};
const logListStyle = {
  display: 'flex', flexDirection: 'column', gap: 6,
  maxHeight: 240, overflowY: 'auto',
};
const logRowStyle = (success) => ({
  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
  background: success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
  border: `1px solid ${success ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.25)'}`,
  borderRadius: 8, fontSize: '0.82rem',
});
const logIconStyle = (success) => ({
  color: success ? '#10b981' : '#ef4444',
  fontSize: '1rem', fontWeight: 700,
});


const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={searchIconStyle}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);


const SendingCard = ({ currentName, remaining, sent, total }) => {
  const pct = total > 0 ? (sent / total) * 100 : 0;
  return (
    <div style={sendingCardStyle}>
      <div style={sendingPulseStyle} />
      <div style={sendingIconStyle}>✉️</div>
      <div style={sendingTitleStyle}>Sent PO email to</div>
      <div style={sendingNameStyle}>{currentName || '…'}</div>
      <div style={sendingRemainingStyle}>
        {remaining > 0 ? `${remaining} email${remaining !== 1 ? 's' : ''} left to send` : 'Finishing up…'}
      </div>
      <div style={progressTrackStyle}>
        <div style={progressFillStyle(pct)} />
      </div>
      <div style={progressCountStyle}>{sent} of {total} sent ({Math.round(pct)}%)</div>
    </div>
  );
};


const EmailCard = ({ row }) => (
  <div
    style={cardStyle}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(34,211,238,0.35)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(34,211,238,0.15)'; }}
  >
    <div style={cardPoIdStyle}>PO {row[1] || '—'}</div>
    <div style={cardEmailStyle}>{row[3] || '—'}</div>
    <div style={cardFileStyle} title={row[4]}>📎 {row[4] || '—'}</div>
  </div>
);


const SendSubbiePOEmail = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentName, setCurrentName] = useState('');
  const [remaining, setRemaining] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [totalToSend, setTotalToSend] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const sendingRef = useRef(false);

  // Animation keyframes
  useEffect(() => {
    const id = 'snl-po-emails-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.innerHTML = `
      @keyframes snlPulse {
        0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
        50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.8; }
      }
      @keyframes snlBounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchPoEmailQueue();
      if (res.status !== 'success') throw new Error(res.error || 'Fetch failed');
      setRows(res.email || []);
    } catch (e) {
      setError(e.message || 'Failed to load email queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      String(r[1] || '').toLowerCase().includes(q) ||   // PO ID
      String(r[3] || '').toLowerCase().includes(q)      // Email
    );
  }, [rows, search]);

  const startBulk = () => {
    if (sendingRef.current || rows.length === 0) return;
    sendingRef.current = true;
    setSending(true);
    setConfirmOpen(false);
    setLog([]);
    setShowLog(false);
    setSentCount(0);
    setRemaining(rows.length);
    setTotalToSend(rows.length);
    setCurrentName('');

    sendABNPOEmail({
      onProgress: (name, remainingCount) => {
        setCurrentName(name);
        setRemaining(remainingCount);
        setSentCount((s) => s + 1);
        setLog((l) => [...l, { name, success: true }]);
        setRows((prev) => prev.slice(1));
      },
      onComplete: () => {
        sendingRef.current = false;
        setSending(false);
        setShowLog(true);
      },
      onEmpty: () => {
        setLog((l) => [...l, { name: '(queue empty)', success: false, error: 'No emails to process' }]);
        sendingRef.current = false;
        setSending(false);
        setShowLog(true);
      },
      onError: (msg) => {
        setLog((l) => [...l, { name: currentName || '(unknown)', success: false, error: msg }]);
        sendingRef.current = false;
        setSending(false);
        setShowLog(true);
      },
    });
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: 'center', padding: 60, color: 'rgba(255,255,255,0.5)' }}>
          Loading PO email queue…
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerWrapStyle}>
        <div style={titleBlockStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px'}}>
          <h2 style={titleStyle}>Send PO Emails</h2>
          
          <HelpButton helpData={[
            { chip: 'subbiepoemailsall', urlFilter: '/static/pdffilters/SendPOEmails.pdf' }
          ]} />
          </div>
          <div style={subtitleStyle}>Bulk-send purchase orders to subcontractors</div>
        </div>
 
        
          
        
        {!sending && rows.length > 0 && (
          <button
            type="button"
            style={bulkBtnStyle}
            onClick={() => setConfirmOpen(true)}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            ✉️ Send Bulk Emails
          </button>
        )}
      </div>
      {pdfUrl && (
          <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
        )}
      {/* Stats */}
      <div style={statsCardStyle}>
        <div style={statsGradientBarStyle} />
        <div style={statsInnerStyle}>
          <div>
            <div style={statsLabelStyle}>Emails Queued</div>
            <div style={statsAmountStyle}>{rows.length}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={statsLabelStyle}>Status</div>
            <div style={pendingBadgeStyle}>{sending ? 'Sending' : 'Ready'}</div>
          </div>
        </div>
        <div style={statsCountStyle}>
          {rows.length} purchase order{rows.length !== 1 ? 's' : ''} awaiting delivery
        </div>
      </div>

      {error && (
        <div style={{ padding: 16, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#fca5a5', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {sending ? (
        <SendingCard
          currentName={currentName}
          remaining={remaining}
          sent={sentCount}
          total={totalToSend}
        />
      ) : rows.length === 0 ? (
        <div style={emptyStateStyle}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12, opacity: 0.4 }}>📭</div>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
            No PO emails to send
          </div>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
            Generate PO PDFs first — they'll show up here when ready.
          </div>
        </div>
      ) : (
        <>
          <div style={searchWrapStyle}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by PO ID or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={searchInputStyle}
            />
          </div>

          {filtered.length === 0 ? (
            <div style={{ ...emptyStateStyle, padding: 30 }}>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                No records match “{search}”.
              </div>
            </div>
          ) : (
            <div style={gridStyle}>
              {filtered.map((r) => (
                <EmailCard key={r[0]} row={r} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Log */}
      {showLog && log.length > 0 && (
        <div style={logCardStyle}>
          <div style={logHeaderStyle}>
            <div style={logTitleStyle}>
              Delivery Log — {log.filter((l) => l.success).length} sent
              {log.some((l) => !l.success) && `, ${log.filter((l) => !l.success).length} failed`}
            </div>
            <button type="button" style={logCloseBtnStyle} onClick={() => setShowLog(false)}>
              Dismiss
            </button>
          </div>
          <div style={logListStyle}>
            {log.map((entry, i) => (
              <div key={i} style={logRowStyle(entry.success)}>
                <span style={logIconStyle(entry.success)}>{entry.success ? '✓' : '✗'}</span>
                <span style={{ color: '#f1f5f9', fontWeight: 600 }}>{entry.name}</span>
                {!entry.success && entry.error && (
                  <span style={{ color: '#fca5a5', fontSize: '0.75rem', marginLeft: 'auto' }}>
                    {entry.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && setConfirmOpen(false)}>
          <div style={confirmModalStyle}>
            <div style={statsGradientBarStyle} />
            <div style={confirmHeaderStyle}>
              <h5 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
                Send {rows.length} PO email{rows.length !== 1 ? 's' : ''}?
              </h5>
            </div>
            <div style={confirmBodyStyle}>
              About to send <strong style={{ color: '#22d3ee' }}>{rows.length}</strong> purchase order{' '}
              email{rows.length !== 1 ? 's' : ''} from{' '}
              <strong style={{ color: '#f1f5f9' }}>SNL Commercial Property Services</strong>.
              <br /><br />
              Each subcontractor will receive their PO PDF as an attachment. This can't be undone.
            </div>
            <div style={confirmFooterStyle}>
              <button type="button" style={cancelBtnStyle} onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button type="button" style={confirmBtnStyle} onClick={startBulk}>
                Send {rows.length} email{rows.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendSubbiePOEmail;